import { authHeaders, fetchWithAuthRetry, fetchWithTimeout, getApiUrl, parseApiError } from './common';
import { RefreshTransientError, RefreshUnauthorizedError } from './error';
import { LOGOUT_REASON } from '../authSession';
import { portalOAuthClientId } from '../portalAuth';
import type { TeamRole } from './teams';

// refresh 401 응답에서 세션 제한 축출을 식별하는 서버 상세 코드(API SSOT와 문자열 일치).
const SESSION_LIMIT_DETAIL_CODE = 'SESSION_LIMIT_EXCEEDED';

export type SocialAuthProvider = 'google' | 'github' | 'apple' | 'microsoft';

export type ThemeMode = 'SYSTEM' | 'LIGHT' | 'DARK';

export type AppLocale = 'en' | 'ko';

export type MemberSummary = {
  id: string;
  email: string;
  nickname: string;
  tier: 'FREE' | 'PRO';
  themeMode: ThemeMode;
  locale: AppLocale;
  isSystemAdmin: boolean;
  isBetaUser: boolean;
  createdAt: string;
  updatedAt: string;
};

export type OnboardingUserState = 'NEW' | 'REACTIVATED' | 'EXISTING';

export type AuthResult = {
  token?: string;
  refreshToken?: string;
  member?: MemberSummary;
  onboarding?: {
    userState: OnboardingUserState;
  };
  pendingSocialSignup?: {
    pendingToken: string;
  };
};

type AuthResponse = {
  data: AuthResult;
};

type MemberEnvelopeResponse = {
  data: MemberSummary;
};

type MessageEnvelopeResponse = {
  data: {
    message: string;
  };
};

export type InvitationType = 'TEAM' | 'PROJECT';
export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED';

export type InvitationPublicInfo = {
  id: string;
  type: InvitationType;
  teamId: string | null;
  projectId: string | null;
  projectRoleId: string | null;
  projectRoleName: string | null;
  teamName: string | null;
  projectName: string | null;
  role: TeamRole;
  status: InvitationStatus;
  expiresAt: string;
  maskedEmail: string;
};

export type MemberInvitationResult = {
  invited: true;
  email: string;
};

type InvitationEnvelopeResponse = {
  data: InvitationPublicInfo;
};

export type AcceptInvitationResult = {
  type: InvitationType;
  teamId: string | null;
  projectId: string | null;
};

type AcceptInvitationEnvelopeResponse = {
  data: AcceptInvitationResult;
};

const authLocaleHeaders = { 'X-AgentTeams-Locale': 'en' };

const socialAuthPaths: Record<SocialAuthProvider, string> = {
  google: '/api/auth/google',
  github: '/api/auth/github/login',
  apple: '/api/auth/apple',
  microsoft: '/api/auth/microsoft',
};

export const buildSocialAuthStartUrl = (provider: SocialAuthProvider, redirect?: string | null) => {
  const url = new URL(`${getApiUrl()}${socialAuthPaths[provider]}`);
  const normalizedRedirect = redirect?.trim();
  if (normalizedRedirect) {
    url.searchParams.set('redirect', normalizedRedirect);
  }

  return url.toString();
};

export const register = async (input: {
  email: string;
  nickname: string;
  password: string;
  inviteCode?: string;
  isTermsAgreed: boolean;
  isPrivacyAgreed: boolean;
}): Promise<AuthResult> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authLocaleHeaders },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to register: ${response.status}`, { skipRedirectOn401: true });
  }

  return ((await response.json()) as AuthResponse).data;
};

export const login = async (input: { email: string; password: string }): Promise<AuthResult> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authLocaleHeaders },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to login: ${response.status}`, { skipRedirectOn401: true });
  }

  return ((await response.json()) as AuthResponse).data;
};

export const completeSocialSignup = async (input: {
  pendingToken: string;
  isTermsAgreed: boolean;
  isPrivacyAgreed: boolean;
}): Promise<AuthResult> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/auth/social/complete-signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authLocaleHeaders },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to complete social signup: ${response.status}`, {
      skipRedirectOn401: true,
    });
  }

  return ((await response.json()) as AuthResponse).data;
};

export const forgotPassword = async (input: { email: string }): Promise<string> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authLocaleHeaders },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to request password reset: ${response.status}`, {
      skipRedirectOn401: true,
    });
  }

  const data = (await response.json()) as MessageEnvelopeResponse;
  return data.data.message;
};

export const resetPassword = async (input: { token: string; password: string }): Promise<string> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authLocaleHeaders },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to reset password: ${response.status}`, { skipRedirectOn401: true });
  }

  const data = (await response.json()) as MessageEnvelopeResponse;
  return data.data.message;
};

export type ActiveSession = {
  id: string;
  kind: 'WEB' | 'DESKTOP';
  deviceInfo: string | null;
  createdAt: string;
  isCurrent: boolean;
};

export type RefreshedTokens = {
  token: string;
  refreshToken?: string;
};

export const refreshAccessToken = async (refreshToken: string): Promise<RefreshedTokens> => {
  let response: Response;
  try {
    response = await fetchWithTimeout(`${getApiUrl()}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authLocaleHeaders },
      body: JSON.stringify({ refreshToken }),
    });
  } catch (error) {
    throw new RefreshTransientError(error instanceof Error ? error.message : undefined);
  }

  if (response.status === 401) {
    // 서버가 확인한 세션 제한 축출만 종료 사유로 보존한다. 그 외 401은 사유 없이 일반 처리한다.
    let reason: string | undefined;
    try {
      const body = (await response.json()) as { errorDetailCode?: unknown };
      if (body?.errorDetailCode === SESSION_LIMIT_DETAIL_CODE) {
        reason = LOGOUT_REASON.SESSION_LIMIT;
      }
    } catch {}
    throw new RefreshUnauthorizedError(reason);
  }

  if (!response.ok) {
    throw new RefreshTransientError(`Refresh failed with status ${response.status}`);
  }

  const data = (await response.json()) as { data: { token: string; refreshToken?: string } };
  return {
    token: data.data.token,
    refreshToken:
      typeof data.data.refreshToken === 'string' && data.data.refreshToken.length > 0
        ? data.data.refreshToken
        : undefined,
  };
};

export const logoutApi = async (token: string, refreshToken?: string): Promise<void> => {
  await fetchWithAuthRetry(`${getApiUrl()}/api/auth/logout`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ refreshToken }),
  }).catch(() => {});
};

export const logoutAllApi = async (token: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/auth/logout-all`, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to logout all: ${response.status}`);
  }
};

export const fetchActiveSessions = async (token: string, refreshToken?: string): Promise<ActiveSession[]> => {
  const url = new URL(`${getApiUrl()}/api/auth/sessions`);
  if (refreshToken) {
    url.searchParams.set('refreshToken', refreshToken);
  }
  const response = await fetchWithAuthRetry(url.toString(), {
    headers: { ...authHeaders(token), ...authLocaleHeaders },
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch sessions: ${response.status}`);
  }

  const data = (await response.json()) as { data: ActiveSession[] };
  return data.data;
};

export const deleteSession = async (token: string, sessionId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/auth/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete session: ${response.status}`);
  }
};

export const fetchInvitationByToken = async (token: string): Promise<InvitationPublicInfo> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/invitations/${token}`, {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch invitation: ${response.status}`);
  }

  const data = (await response.json()) as InvitationEnvelopeResponse;
  return data.data;
};

export const acceptInvitationByToken = async (
  authToken: string,
  invitationToken: string,
): Promise<AcceptInvitationResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/invitations/${invitationToken}/accept`, {
    method: 'POST',
    headers: authHeaders(authToken),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to accept invitation: ${response.status}`);
  }

  const data = (await response.json()) as AcceptInvitationEnvelopeResponse;
  return data.data;
};

export const updateMyProfile = async (
  token: string,
  input: {
    nickname?: string;
    themeMode?: ThemeMode;
    locale?: AppLocale;
    isBetaUser?: boolean;
  },
): Promise<MemberSummary> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/members/me`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update profile: ${response.status}`);
  }

  const data = (await response.json()) as { data: MemberSummary };
  return data.data;
};

export const fetchMyProfile = async (token: string): Promise<MemberSummary> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/members/me`, {
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch profile: ${response.status}`, { skipRedirectOn401: true });
  }

  return ((await response.json()) as MemberEnvelopeResponse).data;
};

export const withdrawMe = async (
  token: string,
  input: {
    password: string;
  },
): Promise<string> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/members/me`, {
    method: 'DELETE',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to withdraw member: ${response.status}`, { skipRedirectOn401: true });
  }

  const data = (await response.json()) as MessageEnvelopeResponse;
  return data.data.message;
};

export type PersonalTokenAuthorization = {
  code: string;
  state: string;
  redirectUri: string;
  expiresAt: string;
};

/**
 * PKCE 인가 코드를 발급받는다.
 *
 * 이 경로에서는 **비밀 원문이 브라우저를 통해 로컬로 넘어가지 않는다**. 웹은 단기 1회용
 * 인가 코드만 받아 전달하고, 실제 토큰 교환은 code_verifier를 가진 클라이언트(CLI/Desktop)가
 * 서버와 직접 수행한다.
 */
export const createPersonalTokenAuthorization = async (
  token: string,
  input: {
    clientId: string;
    redirectUri: string;
    state: string;
    codeChallenge: string;
    scope?: 'read' | 'write';
  },
): Promise<PersonalTokenAuthorization> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/auth/desktop/authorize`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({
      clientId: input.clientId,
      redirectUri: input.redirectUri,
      state: input.state,
      codeChallenge: input.codeChallenge,
      codeChallengeMethod: 'S256',
      ...(input.scope ? { scope: input.scope } : {}),
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create authorization code: ${response.status}`);
  }

  return ((await response.json()) as { data: PersonalTokenAuthorization }).data;
};

export type PortalAuthorization = {
  code: string;
  state: string;
  redirectUri: string;
  expiresAt: string;
};

/**
 * Portal 핸드오프용 PKCE 인가 코드를 발급받는다.
 *
 * 웹 세션으로만 호출할 수 있는 endpoint다. 웹은 단기 1회용 코드만 받아 Portal
 * 콜백으로 전달하고, 코드·토큰을 저장하지 않는다. 실제 세션 교환은 verifier를
 * 가진 Portal이 서버와 직접 수행한다.
 */
export const createPortalAuthorization = async (
  token: string,
  input: {
    clientId: string;
    redirectUri: string;
    state: string;
    codeChallenge: string;
  },
): Promise<PortalAuthorization> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/auth/portal/authorize`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({
      clientId: input.clientId,
      redirectUri: input.redirectUri,
      state: input.state,
      codeChallenge: input.codeChallenge,
      codeChallengeMethod: 'S256',
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create portal authorization code: ${response.status}`);
  }

  return ((await response.json()) as { data: PortalAuthorization }).data;
};

export type PortalTokenExchange = {
  token: string;
  refreshToken: string;
  tokenType: 'Bearer';
  member: {
    memberId: string;
    email: string;
    nickname: string;
  };
};

/**
 * Portal 인가 코드를 웹 세션 토큰으로 교환한다.
 *
 * 인증 없이 호출되는 공개 endpoint이며, 코드+verifier 검증은 서버가 담당한다.
 * 응답의 member는 최소 식별자뿐이라, 세션 저장 전에 `fetchMyProfile`로 전체
 * 프로필을 다시 읽는다(social-complete와 같은 저장 경로).
 */
export const exchangePortalAuthorizationCode = async (input: {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}): Promise<PortalTokenExchange> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/auth/portal/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grantType: 'authorization_code',
      clientId: portalOAuthClientId,
      code: input.code,
      codeVerifier: input.codeVerifier,
      redirectUri: input.redirectUri,
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to exchange portal authorization code: ${response.status}`, {
      skipRedirectOn401: true,
    });
  }

  return ((await response.json()) as { data: PortalTokenExchange }).data;
};

export type DeviceAuthorizationSession = {
  userCode: string;
  clientId: string;
  scope: 'read' | 'write';
  flow: 'login' | 'setup';
  projectName: string | null;
  osType: string | null;
  machineId: string | null;
  /**
   * RSA-OAEP로 암호화된 작업 디렉터리. 승인 화면은 복호화하지 않고 AgentConfig 생성/갱신에
   * 그대로 넘긴다 — loopback 경로의 `ap` 쿼리 파라미터와 같은 값, 같은 용도다.
   */
  authPathEnc: string | null;
  expiresAt: string;
};

/**
 * device authorization(RFC 8628) 승인 화면용 API.
 *
 * CLI가 표시한 짧은 user code로 요청 내용을 조회한다. `deviceCode`는 어떤 응답에도 실리지
 * 않으므로 브라우저는 세션을 승인/거부만 할 수 있고 토큰을 직접 얻을 수는 없다.
 */
export const getDeviceAuthorizationSession = async (
  token: string,
  userCode: string,
): Promise<DeviceAuthorizationSession> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/auth/desktop/device/session?userCode=${encodeURIComponent(userCode)}`,
    { headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to load device authorization: ${response.status}`);
  }

  return ((await response.json()) as { data: DeviceAuthorizationSession }).data;
};

export const approveDeviceAuthorization = async (
  token: string,
  input: {
    userCode: string;
    teamId?: string;
    projectId?: string;
    agentConfigId?: string;
    agentName?: string;
    seedPlanId?: string | null;
  },
): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/auth/desktop/device/approve`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({
      userCode: input.userCode,
      ...(input.teamId ? { teamId: input.teamId } : {}),
      ...(input.projectId ? { projectId: input.projectId } : {}),
      ...(input.agentConfigId ? { agentConfigId: input.agentConfigId } : {}),
      ...(input.agentName ? { agentName: input.agentName } : {}),
      ...(input.seedPlanId ? { seedPlanId: input.seedPlanId } : {}),
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to approve device authorization: ${response.status}`);
  }
};

export const denyDeviceAuthorization = async (token: string, userCode: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/auth/desktop/device/deny`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ userCode }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to deny device authorization: ${response.status}`);
  }
};
